import Link from 'next/link';
import styled, { css } from 'styled-components';

import { ConditionalWrapper } from 'shared/components';
import { SearchResultImageGrid } from 'search/components/search/SearchResultImageGrid';
import { ProductTemplateImageGrid } from 'productTemplate/components/windows/ProductTemplateImageGrid';
import { getNameAlignment } from 'search/utils/getNameAlignment';
import { Loader } from 'shared/components/Loader';
import { variableFontRegular } from 'styles/theme';
import { hideScrollbars } from 'styles/utils/hideScrollbars';

export interface IWindowItemGridProps {
  headerLink?: string;
  imageSize?: string;
  isClickable?: boolean;
  isCrawler?: boolean;
  isLoading?: boolean;
  isScrollable: boolean;
  items: any;
  recommended?: boolean;
  title?: string;
}

export const WindowItemGrid = ({
  headerLink,
  imageSize = '50px',
  isClickable = true,
  isCrawler = false,
  isLoading,
  isScrollable,
  items,
  recommended,
  title,
}: IWindowItemGridProps) => {
  if (isLoading && !isCrawler) {
    return (
      <WindowItemGrid.Container>
        <Loader />
      </WindowItemGrid.Container>
    );
  }

  if (items?.length === 0) {
    return null;
  }

  return (
    <WindowItemGrid.Wrapper $isCrawler={isCrawler}>
      <ConditionalWrapper
        if={!!headerLink}
        with={(_children) => (
          <Link href={headerLink} passHref prefetch={false}>
            <WindowItemGrid.HeaderLink data-qa="window_item_grid_header_link">
              {_children}
            </WindowItemGrid.HeaderLink>
          </Link>
        )}
      >
        {title && <WindowItemGrid.Heading>{title}</WindowItemGrid.Heading>}
      </ConditionalWrapper>
      {recommended && !isCrawler ? (
        <WindowItemGrid.Items isScrollable={isScrollable} imageSize={imageSize}>
          {items?.map((item, index) => (
            <ProductTemplateImageGrid
              align={getNameAlignment(index, 4)}
              imageSize={imageSize}
              key={item?.slug}
              product={item}
              isCrawler={isCrawler}
            />
          ))}
        </WindowItemGrid.Items>
      ) : (
        <WindowItemGrid.Items isScrollable={isScrollable} imageSize={imageSize}>
          {items?.map((item, index) => (
            <SearchResultImageGrid
              align={getNameAlignment(index, 4)}
              imageSize={imageSize}
              key={item?.data?.id}
              product={item}
              isClickable={isClickable}
              isCrawler={isCrawler}
            />
          ))}
        </WindowItemGrid.Items>
      )}
    </WindowItemGrid.Wrapper>
  );
};

WindowItemGrid.Wrapper = styled.div<{ $isCrawler: boolean }>`
  border-bottom: ${({ theme }) => theme.borders.black};
  padding-top: 16px;
  ${({ $isCrawler }) => $isCrawler && `padding-bottom: 16px;`}
`;

WindowItemGrid.HeaderLink = styled.a`
  :hover {
    text-decoration: underline;
  }
`;

WindowItemGrid.Heading = styled.h3`
  font-weight: 400;
  padding-left: 16px;
  text-transform: capitalize;

  ${variableFontRegular({ height: '14px', size: '9.5px' })}
`;

WindowItemGrid.Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 68px;
  padding: 16px;
`;

WindowItemGrid.Items = styled.div<{ isScrollable: boolean; imageSize: string }>`
  display: grid;
  grid-gap: 14px;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 0 16px 16px;
  ${({ isScrollable, imageSize }) =>
    isScrollable
      ? css`
          grid-template-columns: repeat(8, ${imageSize});
          grid-template-rows: ${imageSize};
          min-height: ${imageSize};
        `
      : css`
          grid-template-columns: repeat(4, ${imageSize});
          grid-template-rows: ${imageSize};
          min-height: 150px;
        `}

  ${hideScrollbars}

  div:last-child {
    margin-right: 0;
  }
`;
